import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Upload, Store, User, FileText, AlertCircle, CheckCircle, Loader2, Clock, XCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { sellersApi } from '@/services/api/sellers';
import useAuthStore from '@/store/authStore';
import { useNavigate } from 'react-router-dom';

// Esquema de validación basado en la tabla sellers de la BD
const sellerApplicationSchema = z.object({
    store_name: z.string()
        .min(3, 'El nombre de la tienda debe tener al menos 3 caracteres')
        .max(100, 'El nombre de la tienda no puede exceder 100 caracteres'),
    store_description: z.string()
        .min(10, 'La descripción debe tener al menos 10 caracteres')
        .max(1000, 'La descripción no puede exceder 1000 caracteres')
        .optional(),
    business_license: z.string()
        .min(8, 'La licencia de negocio debe tener al menos 8 caracteres')
        .max(100, 'La licencia no puede exceder 100 caracteres')
        .optional(),
    tax_id: z.string()
        .min(8, 'El RUC/DNI debe tener al menos 8 caracteres')
        .max(50, 'El documento no puede exceder 50 caracteres'),
    business_address: z.string()
        .min(10, 'La dirección debe tener al menos 10 caracteres')
        .max(500, 'La dirección no puede exceder 500 caracteres'),
    business_type: z.enum(['individual', 'company'], {
        required_error: 'Selecciona el tipo de negocio'
    }),
    contact_phone: z.string()
        .min(9, 'El teléfono debe tener al menos 9 dígitos')
        .regex(/^[0-9+\-\s()]+$/, 'Formato de teléfono inválido'),
    experience_years: z.number()
        .min(0, 'Los años de experiencia no pueden ser negativos')
        .max(50, 'Los años de experiencia no pueden exceder 50')
        .optional(),
    terms_accepted: z.boolean()
        .refine(value => value === true, 'Debes aceptar los términos y condiciones')
});

type SellerApplicationForm = z.infer<typeof sellerApplicationSchema>;

interface SellerApplicationComponentProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

const SellerApplicationComponent: React.FC<SellerApplicationComponentProps> = ({
    onSuccess,
    onCancel
}) => {
    const { logout } = useAuthStore();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [sellerStatus, setSellerStatus] = useState<'none' | 'pending' | 'verified' | 'rejected'>('none');
    const [storeLogo, setStoreLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] = useState<string>('');

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        //setValue
    } = useForm<SellerApplicationForm>({
        resolver: zodResolver(sellerApplicationSchema),
        defaultValues: {
            business_type: 'individual',
            experience_years: 0,
            terms_accepted: false,
            store_description: '',
            business_license: '',
            business_address: '',
            contact_phone: ''
        }
    });

    const businessType = watch('business_type');

    // Verificar estado del vendedor al cargar el componente
    useEffect(() => {
        const checkSellerStatus = async () => {
            setIsLoading(true);
            try {
                // Llamada real a la API para verificar el estado
                // Descomenta estas líneas cuando tengas el endpoint listo:

                const response = await sellersApi.getSellerStatus();
                console.log('Seller status:', response);
                setSellerStatus(response?.verification_status);

                // Mock: Simular verificación del estado
                // Reemplaza esto con la llamada real de arriba
                //const mockUserType = response?.user_type; // Cambiar por user?.user_type
                //const mockHasPendingApplication = false; // Cambiar por respuesta real del API
                if (!response) {
                    setSellerStatus('none');
                } else {
                    setSellerStatus(response?.verification_status);
                }
                /*if (mockUserType === 'seller') {
                    setSellerStatus('verified');
                } else if (mockHasPendingApplication) {
                    setSellerStatus('pending');
                } else {
                    setSellerStatus('none');
                }*/

                // Simular delay de carga
                await new Promise(resolve => setTimeout(resolve, 1000));
            } catch (error: any) {
                console.error('Error checking seller status:', error);
                // Si hay error 404, significa que no tiene solicitud
                if (error?.response?.status === 404) {
                    setSellerStatus('none');
                } else {
                    toast.error('Error al verificar estado de vendedor');
                    setSellerStatus('none');
                }
            } finally {
                setIsLoading(false);
            }
        };

        checkSellerStatus();
    }, []);

    const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validar tipo de archivo
            if (!file.type.startsWith('image/')) {
                toast.error('Solo se permiten archivos de imagen');
                return;
            }

            // Validar tamaño (máximo 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('La imagen no puede exceder 5MB');
                return;
            }

            setStoreLogo(file);

            // Crear preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setLogoPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const onSubmit = async (data: SellerApplicationForm) => {
        setIsSubmitting(true);

        try {
            console.log('Datos del formulario:', data);
            console.log('Logo de la tienda:', storeLogo);

            // Aquí iría la llamada real a la API
            const seller = await sellersApi.registerAsSeller({
                store_name: data.store_name,
                store_description: data.store_description,
                business_license: data.business_license,
                tax_id: data.tax_id,
                business_address: data.business_address,
                business_type: data.business_type,
                contact_phone: data.contact_phone,
                experience_years: data.experience_years
            });

            // Si hay logo, subirlo después del registro
            if (storeLogo && seller) {
                await sellersApi.uploadStoreLogo(storeLogo);
            }

            // Simular delay de la API
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Después del éxito, actualizar el estado local
            setSellerStatus('pending');
            toast.success('¡Solicitud enviada exitosamente! Te contactaremos pronto.');

            // No llamar onSuccess aquí porque ahora mostramos el estado pendiente
            // onSuccess?.();
            if (seller && sellerStatus == 'verified') {
                onSuccess?.();
            }

        } catch (error: any) {
            console.error('Error al enviar solicitud:', error);

            // Manejar diferentes tipos de errores
            if (error.response?.status === 422) {
                toast.error('Por favor revisa los datos ingresados.');
            } else if (error.response?.status === 409) {
                toast.error('Ya tienes una solicitud pendiente o eres vendedor.');
                // Si hay conflicto, verificar el estado nuevamente
                const checkStatus = async () => {
                    try {
                        // const response = await sellersApi.getSellerStatus();
                        // setSellerStatus(response.status);
                    } catch (e) {
                        console.error('Error checking status after conflict:', e);
                    }
                };
                checkStatus();
            } else {
                toast.error('Error al enviar la solicitud. Inténtalo nuevamente.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmit(onSubmit)();
    };

    // Mostrar loading mientras verifica el estado
    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Verificando estado de la solicitud...</p>
                </div>
            </div>
        );
    }
    const handleLogout = () => {
        logout();
        navigate('/');
    };
    // Si ya es vendedor verificado
    if (sellerStatus === 'verified') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center p-6">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        ¡Ya eres vendedor!
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Tu cuenta de vendedor ya está activa.
                        Para acceder a tu panel de vendedor para gestionar tus productos y ventas, por favor Reinicia Sesión
                    </p>
                    <div className="space-y-3">
                        <button
                            //onClick={() => window.location.href = '/seller/dashboard'}
                            onClick={() => {
                                handleLogout();
                            }}
                            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            Cerrar Sesión e ir a Login
                        </button>
                        {onCancel && (
                            <button
                                onClick={onCancel}
                                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Volver al Inicio
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Si tiene solicitud pendiente
    if (sellerStatus === 'pending') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center p-6">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Clock className="w-8 h-8 text-yellow-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Solicitud en Revisión
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Ya tienes una solicitud para ser vendedor en proceso de revisión.
                        Nuestro equipo está evaluando tu información y te contactaremos pronto con el resultado.
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <h3 className="font-medium text-yellow-800 mb-2">Estado actual:</h3>
                        <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                            <span className="text-sm text-yellow-700">Pendiente de revisión</span>
                        </div>
                        <p className="text-xs text-yellow-600 mt-2">
                            Tiempo estimado: 24-48 horas
                        </p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                        <h3 className="font-medium text-yellow-800 mb-2">Importante:</h3>
                        <p className="text-xs text-yellow-600 mt-2">
                            Podrías necesitar reloguear tu cuenta para actualizar la información.
                        </p>
                    </div>
                    <div className="space-y-3">
                        {/* <button
                            onClick={() => toast('Te notificaremos por email cuando tengamos novedades')}
                            className="w-full px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                        >
                            Recibir Notificaciones
                        </button> */}
                        {onCancel && (
                            <button
                                onClick={onCancel}
                                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Volver al Inicio
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Si la solicitud fue rechazada
    if (sellerStatus === 'rejected') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="max-w-md mx-auto text-center p-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Solicitud No Aprobada
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Tu solicitud anterior para ser vendedor no fue aprobada.
                        Puedes enviar una nueva solicitud con información actualizada.
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <h3 className="font-medium text-red-800 mb-2">Motivos comunes:</h3>
                        <ul className="text-sm text-red-700 space-y-1 text-left">
                            <li>• Documentos incompletos o incorrectos</li>
                            <li>• Información comercial insuficiente</li>
                            <li>• No cumple con los requisitos mínimos</li>
                        </ul>
                    </div>
                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                setSellerStatus('none');
                                toast('Puedes completar una nueva solicitud');
                            }}
                            className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                        >
                            Nueva Solicitud
                        </button>
                        {onCancel && (
                            <button
                                onClick={onCancel}
                                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                Volver al Inicio
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
                        <Store className="w-8 h-8 text-primary-600" />
                    </div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Conviértete en Vendedor
                    </h1>
                    <p className="text-gray-600 max-w-2xl mx-auto">
                        Únete a miles de vendedores exitosos y comienza a vender tus productos en nuestra plataforma.
                        Completa este formulario y nuestro equipo revisará tu solicitud.
                    </p>
                </div>

                {/* Beneficios */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">¿Por qué vender con nosotros?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Millones de clientes</h3>
                                <p className="text-sm text-gray-600">Accede a una base de clientes en crecimiento</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Herramientas potentes</h3>
                                <p className="text-sm text-gray-600">Panel de control completo para gestionar tu negocio</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-gray-900">Soporte 24/7</h3>
                                <p className="text-sm text-gray-600">Equipo de soporte dedicado para vendedores</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Formulario */}
                <div className="space-y-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <Store className="w-5 h-5 mr-2" />
                            Información de la Tienda
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Logo de la tienda */}
                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Logo de la Tienda (Opcional)
                                </label>
                                <div className="flex items-center space-x-4">
                                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden">
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <Upload className="w-8 h-8 text-gray-400" />
                                        )}
                                    </div>
                                    <div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                            id="logo-upload"
                                        />
                                        <label
                                            htmlFor="logo-upload"
                                            className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            Subir Logo
                                        </label>
                                        <p className="text-xs text-gray-500 mt-1">PNG, JPG hasta 5MB</p>
                                    </div>
                                </div>
                            </div>

                            {/* Nombre de la tienda */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre de la Tienda *
                                </label>
                                <input
                                    type="text"
                                    {...register('store_name')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.store_name ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="Mi Tienda Increíble"
                                />
                                {errors.store_name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.store_name.message}</p>
                                )}
                            </div>

                            {/* Teléfono de contacto */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Teléfono de Contacto *
                                </label>
                                <input
                                    type="tel"
                                    {...register('contact_phone')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.contact_phone ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="+51 999 999 999"
                                />
                                {errors.contact_phone && (
                                    <p className="mt-1 text-sm text-red-600">{errors.contact_phone.message}</p>
                                )}
                            </div>

                            {/* Años de experiencia */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Años de Experiencia (Opcional)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="50"
                                    {...register('experience_years', { valueAsNumber: true })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.experience_years ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="0"
                                />
                                {errors.experience_years && (
                                    <p className="mt-1 text-sm text-red-600">{errors.experience_years.message}</p>
                                )}
                            </div>

                            {/* Descripción de la tienda */}
                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción de la Tienda (Opcional)
                                </label>
                                <textarea
                                    {...register('store_description')}
                                    rows={4}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.store_description ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="Describe tu tienda, productos que vendes, experiencia, etc..."
                                />
                                {errors.store_description && (
                                    <p className="mt-1 text-sm text-red-600">{errors.store_description.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Información del Negocio */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <User className="w-5 h-5 mr-2" />
                            Información del Negocio
                        </h2>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Tipo de negocio */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo de Negocio *
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="individual"
                                            {...register('business_type')}
                                            className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Persona Natural</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            value="company"
                                            {...register('business_type')}
                                            className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Empresa</span>
                                    </label>
                                </div>
                                {errors.business_type && (
                                    <p className="mt-1 text-sm text-red-600">{errors.business_type.message}</p>
                                )}
                            </div>

                            {/* Documento de identificación fiscal */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {businessType === 'individual' ? 'DNI' : 'RUC'} *
                                </label>
                                <input
                                    type="text"
                                    {...register('tax_id')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.tax_id ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder={businessType === 'individual' ? '12345678' : '20123456789'}
                                    maxLength={businessType === 'individual' ? 8 : 11}
                                />
                                {errors.tax_id && (
                                    <p className="mt-1 text-sm text-red-600">{errors.tax_id.message}</p>
                                )}
                            </div>

                            {/* Licencia de negocio */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Licencia de Funcionamiento (Opcional)
                                </label>
                                <input
                                    type="text"
                                    {...register('business_license')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.business_license ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="LIC-123456"
                                />
                                {errors.business_license && (
                                    <p className="mt-1 text-sm text-red-600">{errors.business_license.message}</p>
                                )}
                            </div>

                            {/* Dirección del negocio */}
                            <div className="lg:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dirección del Negocio *
                                </label>
                                <textarea
                                    {...register('business_address')}
                                    rows={3}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${errors.business_address ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    placeholder="Av. Principal 123, Miraflores, Lima, Perú"
                                />
                                {errors.business_address && (
                                    <p className="mt-1 text-sm text-red-600">{errors.business_address.message}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Términos y condiciones */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <FileText className="w-5 h-5 mr-2" />
                            Términos y Condiciones
                        </h2>

                        <div className="space-y-4">
                            <div className="bg-gray-50 rounded-lg p-4">
                                <h3 className="font-medium text-gray-900 mb-2">Requisitos para ser vendedor:</h3>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Ser mayor de 18 años</li>
                                    <li>• Proporcionar información veraz y actualizada</li>
                                    <li>• Contar con documentos de identificación fiscal válidos</li>
                                    <li>• Cumplir con nuestras políticas de calidad y servicio</li>
                                    <li>• Responder a consultas de clientes en máximo 24 horas</li>
                                    <li>• Mantener un nivel de servicio superior al 90%</li>
                                    <li>• Enviar productos en los tiempos acordados</li>
                                </ul>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4">
                                <h3 className="font-medium text-blue-900 mb-2">Documentos que podrían requerirse:</h3>
                                <ul className="text-sm text-blue-800 space-y-1">
                                    <li>• DNI o RUC vigente</li>
                                    <li>• Licencia de funcionamiento (si aplica)</li>
                                    <li>• Constancia de domicilio fiscal</li>
                                    <li>• Registro sanitario (para productos alimenticios/cosméticos)</li>
                                </ul>
                            </div>

                            <label className="flex items-start space-x-3">
                                <input
                                    type="checkbox"
                                    {...register('terms_accepted')}
                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-1"
                                />
                                <span className="text-sm text-gray-700">
                                    Acepto los{' '}
                                    <a href="/terms" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
                                        términos y condiciones
                                    </a>{' '}
                                    y las{' '}
                                    <a href="/seller-policies" target="_blank" className="text-primary-600 hover:text-primary-700 underline">
                                        políticas para vendedores
                                    </a>
                                    . Declaro que la información proporcionada es veraz y me comprometo a mantenerla actualizada.
                                </span>
                            </label>
                            {errors.terms_accepted && (
                                <p className="text-sm text-red-600">{errors.terms_accepted.message}</p>
                            )}
                        </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-end">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleFormSubmit}
                            disabled={isSubmitting}
                            className="px-8 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center min-w-[200px]"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    Enviando Solicitud...
                                </>
                            ) : (
                                'Enviar Solicitud'
                            )}
                        </button>
                    </div>
                </div>

                {/* Información adicional */}
                <div className="mt-8 space-y-6">
                    {/* Proceso de revisión */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                        <div className="flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                            <div>
                                <h3 className="font-medium text-blue-900 mb-2">Proceso de Revisión</h3>
                                <p className="text-sm text-blue-800 mb-3">
                                    Tu solicitud será revisada por nuestro equipo en un plazo de 24-48 horas.
                                    Te contactaremos por email con el resultado y los siguientes pasos.
                                </p>
                                <div className="bg-blue-100 rounded-lg p-3">
                                    <h4 className="font-medium text-blue-900 text-sm mb-2">Pasos del proceso:</h4>
                                    <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                                        <li>Verificación de documentos y datos</li>
                                        <li>Validación de información fiscal</li>
                                        <li>Revisión de políticas y términos</li>
                                        <li>Aprobación y activación de cuenta</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Comisiones y beneficios */}
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                        <h3 className="font-medium text-green-900 mb-3">Comisiones y Beneficios</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div>
                                <h4 className="font-medium text-green-800 mb-2">Comisiones:</h4>
                                <ul className="text-green-700 space-y-1">
                                    <li>• Comisión estándar: 10% por venta</li>
                                    <li>• Sin cuotas mensuales fijas</li>
                                    <li>• Pago cada 15 días</li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-medium text-green-800 mb-2">Beneficios:</h4>
                                <ul className="text-green-700 space-y-1">
                                    <li>• Panel de gestión completo</li>
                                    <li>• Herramientas de marketing</li>
                                    <li>• Soporte especializado</li>
                                    <li>• Estadísticas detalladas</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SellerApplicationComponent;